import { createContext, type ReactNode, useContext, useMemo } from 'react';
import { Outlet, useOutletContext } from 'react-router';
import { NoAccess } from '@/components/QueryBoundary';
import { composeSlot, type PlatformModule, type SlotContribution, type SlotName, type SlotProps } from './modules';
import { useEnabledModules, usePermissions } from './session';

const ModulesContext = createContext<PlatformModule[]>([]);

/** Hands the shell the modules compiled into this build. Which of them are installed for the organization comes from the bootstrap. */
export function ModulesProvider({ modules, children }: { modules: PlatformModule[]; children: ReactNode }) {
  return <ModulesContext.Provider value={modules}>{children}</ModulesContext.Provider>;
}

/** Every module in this build, installed or not. Use `useEnabledModules()` (ids) with the compose helpers in `modules.ts`. */
export const useModules = () => useContext(ModulesContext);

/**
 * What the installed modules have put into a slot, in order and only what this person may use, for a screen that lays the contributions
 * out itself (as columns, as views) instead of drawing them one after another with `<Slot>`.
 */
export function useSlotContributions<N extends SlotName>(name: N): SlotContribution<N>[] {
  const modules = useModules();
  const enabled = useEnabledModules();
  const { set } = usePermissions();
  return useMemo(() => composeSlot(modules, enabled, set, name), [modules, enabled, set, name]);
}

/** Draws whatever the installed modules have put into a slot of this screen. Empty when none has. */
export function Slot<N extends SlotName>({ name, ...props }: { name: N } & SlotProps[N]) {
  const contributions = useSlotContributions(name);
  return (
    <>
      {contributions.map(({ id, component: Contribution }) => {
        const Component = Contribution as React.ComponentType<SlotProps[N]>;
        return <Component key={id} {...(props as unknown as SlotProps[N])} />;
      })}
    </>
  );
}

/**
 * Route guard for a module's pages: while the module is not installed its routes do not exist, so the address answers "not found"
 * exactly as the server does. Use as a layout (`element: <ModuleGate id="physio" />`) or around a page.
 */
export function ModuleGate({ id, children }: { id: string; children?: ReactNode }) {
  const enabled = useEnabledModules();
  // A bare <Outlet /> would start a fresh outlet context of `undefined`, hiding whatever an ancestor route (e.g. the athlete's
  // page) passed down. Reading and re-passing it here keeps it flowing through this layout to every module's nested routes.
  const context = useOutletContext();
  if (!enabled.includes(id)) return <NoAccess title="Page not found" description="The page you are looking for does not exist." />;
  return <>{children ?? <Outlet context={context} />}</>;
}
